import React, { useContext, useCallback } from 'react';
import UserContext, { UserProfileContext } from '../utils/getContext';
import moment from 'moment';
import PropTypes from 'prop-types';

const Display = ({ type, data, classes = {} }) => {
    const { settings, lang } = useContext(UserContext);
    const { adminPage } = useContext(UserProfileContext) || {};

    const classInt = classes?.value?.classInt || '';
    const classFloat = classes?.value?.classFloat || '';
    const classSeparator = classes?.value?.classSeparator || '';
    const classCurrency = classes?.value?.classCurrency || '';

    const transformFIO = useCallback(
        function (data) {
            if (!data.first_name && !data.last_name && !data.patronymic) {
                return '';
            }
            const firstName =
                `${data.first_name}` !== 'undefined' && `${data.first_name}` !== 'null' ? `${data.first_name} ` : '';
            const lastName = `${data.last_name}` !== 'undefined' && `${data.last_name}` !== 'null' ? `${data.last_name} ` : '';
            const patronymic =
                `${data.patronymic}` !== 'undefined' && `${data.patronymic}` !== 'null' ? `${data.patronymic} ` : '';

            if (settings?.name_format && !adminPage) {
                switch (settings.name_format) {
                    case '0':
                        return <>{(lastName + firstName + patronymic).trim()}</>;
                    case '1':
                        return <>{(firstName + lastName + patronymic).trim()}</>;
                    case '2':
                        return (
                            <>
                                {(
                                    (firstName ? `${firstName.substring(0, 1)}. ` : '') +
                                    (patronymic ? `${patronymic.substring(0, 1)}. ` : null) +
                                    lastName
                                ).trim()}
                            </>
                        );
                    case '3':
                        return (
                            <>
                                {(
                                    lastName +
                                    (firstName ? `${firstName.trim().substring(0, 1)}. ` : '') +
                                    (patronymic ? `${patronymic.trim().substring(0, 1)}. ` : '')
                                ).trim()}
                            </>
                        );
                    case '4':
                        return <>{(firstName + lastName).trim()}</>;
                    case '5':
                        return <>{(lastName + firstName).trim()}</>;
                    default:
                        return <>{(lastName + firstName + patronymic).trim()}</>;
                }
            } else {
                return <>{(lastName + firstName + patronymic).trim()}</>;
            }
        },
        [adminPage, settings]
    );
    const getTimeWithOffset = useCallback(
        (data) => {
            let dating = moment(data.date || data.time, data.format);
            if (settings?.timezone && data.utc) {
                let timezone;
                let plus;
                if (settings?.timezone.indexOf('-') !== -1) {
                    timezone = settings?.timezone.slice(settings?.timezone.indexOf('-') + 1, settings?.timezone.indexOf(')'));
                    plus = true;
                }
                if (settings?.timezone.indexOf('+') !== -1) {
                    timezone = settings?.timezone.slice(settings?.timezone.indexOf('+') + 1, settings?.timezone.indexOf(')'));
                }
                if (!isNaN(+timezone)) {
                    dating = dating.add(plus ? +timezone : 0 - +timezone, 'hours');
                }
            }
            return dating;
        },
        [settings]
    );
    const transformDate = useCallback(
        function (data) {
            if (settings?.date_format && data.date && moment(data.date, data.format).isValid()) {
                let dating = getTimeWithOffset(data);
                switch (settings?.date_format) {
                    case '0':
                        let string = dating.locale(lang).format(data?.withoutDay ? 'MMMM, YYYY' : 'MMMM DD, YYYY');
                        return <>{string.charAt(0).toUpperCase() + string.slice(1)}</>;
                    case '1':
                        return <>{dating.format(data?.withoutDay ? 'YYYY-MM' : 'YYYY-MM-DD')}</>;
                    case '2':
                        return <>{dating.format(data?.withoutDay ? 'MM/YYYY' : 'MM/DD/YYYY')}</>;
                    case '3':
                        return <>{dating.format(data?.withoutDay ? 'MM.YYYY' : 'DD.MM.YYYY')}</>;
                    default:
                        return <>{dating.format(data?.withoutDay ? 'MM.YYYY' : 'DD.MM.YYYY')}</>;
                }
            } else if (data.date && moment(data.date, data.format).isValid()) {
                return <>{moment(data.date, data.format).format('DD.MM.YYYY')}</>;
            } else {
                return <></>;
            }
        },
        [settings, getTimeWithOffset, lang]
    );
    const transformTime = useCallback(
        function (data) {
            if (settings?.time_format && data.time && moment(data.time, data.format).isValid()) {
                const time = getTimeWithOffset(data).locale('en');
                switch (data?.time_format || settings?.time_format) {
                    case '0':
                        return <>{time.format('h:mm a')}</>;
                    case '1':
                        return <>{time.format('h:mm A')}</>;
                    case '2':
                        return <>{time.format('HH:mm')}</>;
                    default:
                        return <>{time.format('HH:mm')}</>;
                }
            } else if (data.time && moment(data.time, 'HH:mm').isValid()) {
                const time = moment(data.time, 'HH:mm').locale('en');
                return <>{time.format('HH:mm')}</>;
            } else {
                return <></>;
            }
        },
        [settings, getTimeWithOffset]
    );
    const transformNumber = useCallback(
        function (data) {
            let customCurrencyNode = data?.customCurrency || null;
            let currencySymbol = data.currency && data.currency.symbol ? data.currency.symbol : '';
            let currencyAbbr = data.currency && data.currency.code ? data.currency.code : '';
            let number = data.value ? (data.formatted ? data.value : parseFloat(data.value)) : 0;

            if (data.formatByDecimal) {
                number = Number.parseFloat(number).toFixed(data?.currency?.displayed_decimals_number);
            }

            if (isNaN(number) && !data?.formatted) {
                return <></>;
            }

            if (!data.value || !data.formatted) {
                let split = `${number}`.split('.');
                let int = split[0] ? split[0] : '';
                let float = split[1] ? split[1] : '00';

                let separator = ',';

                if (settings?.currency_thousands) {
                    switch (settings.currency_thousands) {
                        case '0':
                            separator = '.';
                            break;
                        case '1':
                            separator = ',';
                            break;
                        case '2':
                            separator = ' ';
                            break;
                        case '3':
                            separator = '';
                    }
                }

                int = int.replace(/(\d{1,3}(?=(?:\d\d\d)+(?!\d)))/g, '$1' + separator);

                if (settings?.currency_delimiter && float.length > 0) {
                    let separatorFloat = '.';
                    switch (settings.currency_delimiter) {
                        case '0':
                            separatorFloat = '.';
                            break;
                        case '1':
                            separatorFloat = ',';
                            break;
                        default:
                            separatorFloat = `.`;
                    }
                    number = (
                        <>
                            <span className={classInt}>{int}</span>
                            <span className={classSeparator}>{separatorFloat}</span>
                            <span className={classFloat}>{float}</span>
                        </>
                    );
                } else {
                    number = <span className={classInt}>{int}</span>;
                }
            }

            if (settings?.currency_format && (data.currency || data.customCurrency)) {
                switch (data?.currency_format || settings.currency_format) {
                    case '0':
                        number = (
                            <>
                                <span className={classCurrency}>
                                    {customCurrencyNode ? customCurrencyNode : currencySymbol + ' '}
                                </span>{' '}
                                {number}
                            </>
                        );
                        break;
                    case '1':
                        number = (
                            <>
                                {number}{' '}
                                <span className={classCurrency}>
                                    {customCurrencyNode ? customCurrencyNode : currencySymbol + ''}
                                </span>
                            </>
                        );
                        break;
                    case '2':
                        number = (
                            <>
                                <span className={classCurrency}>
                                    {customCurrencyNode ? customCurrencyNode : currencyAbbr + ' '}
                                </span>{' '}
                                {number}
                            </>
                        );
                        break;
                    case '3':
                        number = (
                            <>
                                {number}{' '}
                                <span className={classCurrency}>
                                    {customCurrencyNode ? customCurrencyNode : currencyAbbr + ''}
                                </span>
                            </>
                        );
                        break;
                    default:
                        number = (
                            <>
                                <span className={classCurrency}>
                                    {customCurrencyNode ? customCurrencyNode : currencySymbol + ' '}
                                </span>{' '}
                                {number}
                            </>
                        );
                }
            } else if (data.currency) {
                number = (
                    <>
                        <span className={classCurrency}>{customCurrencyNode ? customCurrencyNode : currencySymbol + ' '}</span>{' '}
                        {number}
                    </>
                );
            }

            return number;
        },
        [
            classCurrency,
            classFloat,
            classInt,
            classSeparator,
            settings?.currency_delimiter,
            settings?.currency_format,
            settings?.currency_thousands
        ]
    );

    const transformCurrency = useCallback(
        (data) => {
            let currencySymbol = data.currency && data.currency.symbol ? data.currency.symbol : '';
            let currencyAbbr = data.currency && data.currency.code ? data.currency.code : '';
            let currency;

            switch (settings?.currency_format) {
                case '0':
                case '1':
                    currency = currencySymbol;
                    break;

                case '2':
                case '3':
                    currency = currencyAbbr;
                    break;

                default:
                    currency = currencySymbol;
            }
            return currency;
        },
        [settings?.currency_format]
    );

    switch (type) {
        case 'fio':
            return transformFIO(data);
        case 'value':
            return transformNumber(data);
        case 'date':
            return transformDate(data);
        case 'time':
            return transformTime(data);
        case 'currency':
            return transformCurrency(data);
        default:
            return <></>;
    }
};

Display.propTypes = {
    data: PropTypes.object,
    type: PropTypes.oneOf(['fio', 'value', 'date', 'time'])
};

export default Display;
